import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Radio,
  Save,
  Settings,
  Volume2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toaster';
import { useAuth } from '@/hooks/useAuth';
import { getApiBaseUrl } from '@/lib/capacitor';

type MqttMessageKey = 'selectMode' | 'cutoutDisabled' | 'hudText' | 'notifications';

interface MqttMessageSettings {
  topic: string;
  payload: Record<string, unknown>;
}

interface GlobalSettings {
  appDownloadEnabled: boolean;
  mqttBrokerUrl: string;
  n8nWebhookUrl: string;
  mqttMessages: Record<MqttMessageKey, MqttMessageSettings>;
}

const mqttMessageLabels: Record<MqttMessageKey, { title: string; description: string }> = {
  selectMode: {
    title: 'Display Mode',
    description: 'Sent for photo and video pings',
  },
  cutoutDisabled: {
    title: 'Hide HUD Cutout',
    description: 'Sent before visual pings are shown',
  },
  hudText: {
    title: 'HUD Text',
    description: 'Use {{hudText}} for the sanitized message text',
  },
  notifications: {
    title: 'Unread Count',
    description: 'Use {{unreadCount}} for the current unread count',
  },
};

const mqttMessageKeys: MqttMessageKey[] = [
  'selectMode',
  'cutoutDisabled',
  'hudText',
  'notifications',
];

function stringifyPayload(payload: Record<string, unknown>): string {
  return JSON.stringify(payload, null, 2);
}

export default function GlobalSettingsPage() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading, authenticatedFetch } = useAuth();
  const [settings, setSettings] = useState<GlobalSettings | null>(null);
  const [payloadText, setPayloadText] = useState<Record<MqttMessageKey, string>>({
    selectMode: '{}',
    cutoutDisabled: '{}',
    hudText: '{}',
    notifications: '{}',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || user.authType !== 'google' || user.role !== 'admin')) {
      navigate('/login?redirect=/admin/settings');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/settings/global`);
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to load settings');
      }

      const nextSettings = data.settings as GlobalSettings;
      setSettings(nextSettings);
      setPayloadText({
        selectMode: stringifyPayload(nextSettings.mqttMessages.selectMode.payload),
        cutoutDisabled: stringifyPayload(nextSettings.mqttMessages.cutoutDisabled.payload),
        hudText: stringifyPayload(nextSettings.mqttMessages.hudText.payload),
        notifications: stringifyPayload(nextSettings.mqttMessages.notifications.payload),
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to load settings',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateMqttMessage = (
    key: MqttMessageKey,
    updates: Partial<MqttMessageSettings>
  ) => {
    setSettings((current) => {
      if (!current) return current;

      return {
        ...current,
        mqttMessages: {
          ...current.mqttMessages,
          [key]: {
            ...current.mqttMessages[key],
            ...updates,
          },
        },
      };
    });
  };

  const parsePayloads = (): Record<MqttMessageKey, Record<string, unknown>> | null => {
    const parsed = {} as Record<MqttMessageKey, Record<string, unknown>>;

    for (const key of mqttMessageKeys) {
      try {
        const value = JSON.parse(payloadText[key]) as unknown;
        if (!value || typeof value !== 'object' || Array.isArray(value)) {
          throw new Error('Payload must be a JSON object');
        }
        parsed[key] = value as Record<string, unknown>;
      } catch (error) {
        toast({
          title: 'Invalid JSON',
          description: `${mqttMessageLabels[key].title}: ${
            error instanceof Error ? error.message : 'Payload must be valid JSON'
          }`,
          variant: 'destructive',
        });
        return null;
      }
    }

    return parsed;
  };

  const handleSave = async () => {
    if (!settings) return;

    const parsedPayloads = parsePayloads();
    if (!parsedPayloads) return;

    setIsSaving(true);
    try {
      const body: GlobalSettings = {
        ...settings,
        mqttMessages: {
          selectMode: {
            ...settings.mqttMessages.selectMode,
            payload: parsedPayloads.selectMode,
          },
          cutoutDisabled: {
            ...settings.mqttMessages.cutoutDisabled,
            payload: parsedPayloads.cutoutDisabled,
          },
          hudText: {
            ...settings.mqttMessages.hudText,
            payload: parsedPayloads.hudText,
          },
          notifications: {
            ...settings.mqttMessages.notifications,
            payload: parsedPayloads.notifications,
          },
        },
      };

      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/settings/global`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to save settings');
      }

      const savedSettings = data.settings as GlobalSettings;
      setSettings(savedSettings);
      setPayloadText({
        selectMode: stringifyPayload(savedSettings.mqttMessages.selectMode.payload),
        cutoutDisabled: stringifyPayload(savedSettings.mqttMessages.cutoutDisabled.payload),
        hudText: stringifyPayload(savedSettings.mqttMessages.hudText.payload),
        notifications: stringifyPayload(savedSettings.mqttMessages.notifications.payload),
      });
      toast({ title: 'Settings saved' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save settings',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user || user.authType !== 'google' || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Redirecting to login...</div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Settings unavailable</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 glass border-b border-border">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center gap-4">
          <Link to="/admin">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <Settings className="w-5 h-5 text-primary" />
          <h1 className="font-display font-semibold flex-1">Global Settings</h1>
          <Button onClick={handleSave} disabled={isSaving} size="sm">
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 space-y-6">
        <section className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-3 mb-4">
            <Download className="w-5 h-5 text-muted-foreground" />
            <h2 className="font-semibold">App Downloads</h2>
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-medium">Android downloads</p>
              <p className="text-sm text-muted-foreground">
                Controls APK downloads from the public site.
              </p>
            </div>
            <Switch
              checked={settings.appDownloadEnabled}
              onCheckedChange={(appDownloadEnabled) =>
                setSettings((current) => current ? { ...current, appDownloadEnabled } : current)
              }
            />
          </div>
        </section>

        <section className="rounded-xl border border-border bg-card p-4 space-y-4">
          <div className="flex items-center gap-3">
            <Radio className="w-5 h-5 text-muted-foreground" />
            <h2 className="font-semibold">MQTT</h2>
          </div>

          <div className="space-y-2">
            <label htmlFor="mqttBrokerUrl" className="text-sm font-medium">
              Broker URL
            </label>
            <Input
              id="mqttBrokerUrl"
              value={settings.mqttBrokerUrl}
              onChange={(event) =>
                setSettings((current) =>
                  current ? { ...current, mqttBrokerUrl: event.target.value } : current
                )
              }
              placeholder="mqtt://192.168.0.8:1883"
            />
          </div>
        </section>

        <section className="rounded-xl border border-border bg-card p-4 space-y-4">
          <div className="flex items-center gap-3">
            <Volume2 className="w-5 h-5 text-muted-foreground" />
            <h2 className="font-semibold">Sonos</h2>
          </div>

          <div className="space-y-2">
            <label htmlFor="n8nWebhookUrl" className="text-sm font-medium">
              n8n webhook URL
            </label>
            <Input
              id="n8nWebhookUrl"
              value={settings.n8nWebhookUrl}
              onChange={(event) =>
                setSettings((current) =>
                  current ? { ...current, n8nWebhookUrl: event.target.value } : current
                )
              }
              placeholder="https://n8n.example.com/webhook/play-sound"
            />
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex items-center gap-3">
            <Radio className="w-5 h-5 text-muted-foreground" />
            <h2 className="font-semibold">MQTT Messages</h2>
          </div>

          {mqttMessageKeys.map((key) => (
            <div key={key} className="rounded-xl border border-border bg-card p-4 space-y-4">
              <div>
                <h3 className="font-medium">{mqttMessageLabels[key].title}</h3>
                <p className="text-sm text-muted-foreground">
                  {mqttMessageLabels[key].description}
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor={`${key}-topic`} className="text-sm font-medium">
                  Topic
                </label>
                <Input
                  id={`${key}-topic`}
                  value={settings.mqttMessages[key].topic}
                  onChange={(event) => updateMqttMessage(key, { topic: event.target.value })}
                />
              </div>

              <div className="space-y-2">
                <label htmlFor={`${key}-payload`} className="text-sm font-medium">
                  Payload
                </label>
                <Textarea
                  id={`${key}-payload`}
                  value={payloadText[key]}
                  onChange={(event) =>
                    setPayloadText((current) => ({
                      ...current,
                      [key]: event.target.value,
                    }))
                  }
                  className="min-h-32 font-mono text-xs"
                />
              </div>
            </div>
          ))}
        </section>
      </main>
    </div>
  );
}
